import { formatCurrency } from '@web-platform/shared-universal-utils-frontend-localisation';
import { MESSAGE_TYPE } from '@web-platform/shared-universal-utils-universal/src/rank-explainability/messageType';

import { translate } from '../../translate';
import { parseFlightDurationToString } from '../parseFlightDurationToString';

import type { RankExplainabilityMessageType } from '@web-platform/shared-universal-utils-universal/src/rank-explainability/messageType';

export type RankExplainabilityData = {
  priceDifference: number;
  durationDifference: number;
  timeDifference?: number;
} | null;

const buildMessage = (
  messageType: RankExplainabilityMessageType,
  priceDifference: number,
  durationDifference: number,
  timeDifference?: number,
): string | null => {
  const priceDifferenceString = formatCurrency(priceDifference);
  const durationDifferenceString = parseFlightDurationToString(
    { duration: durationDifference },
    true,
  );

  switch (messageType) {
    case MESSAGE_TYPE.MESSAGE_TYPE_IDENTICAL_DIRECT:
      return translate('ktxt_rank_explainability_equivalent_direct');
    case MESSAGE_TYPE.MESSAGE_TYPE_IDENTICAL_FASTER:
      return translate('ktxt_rank_explainability_equivalent_faster');
    case MESSAGE_TYPE.MESSAGE_TYPE_IDENTICAL_NIGHT:
      return translate('ktxt_rank_explainability_equivalent_night_flight');
    case MESSAGE_TYPE.MESSAGE_TYPE_IDENTICAL_NONE:
      return translate('ktxt_rank_explainability_equivalent_none');

    case MESSAGE_TYPE.MESSAGE_TYPE_WITHIN_THRESHOLD_DIRECT:
      return translate('ktxt_rank_explainability_within_threshold_direct', {
        price_diff: priceDifferenceString,
      });
    case MESSAGE_TYPE.MESSAGE_TYPE_WITHIN_THRESHOLD_FASTER:
      return translate('ktxt_rank_explainability_within_threshold_faster', {
        price_diff: priceDifferenceString,
        duration_diff: durationDifferenceString,
      });
    case MESSAGE_TYPE.MESSAGE_TYPE_WITHIN_THRESHOLD_NIGHT:
      return translate('ktxt_rank_explainability_within_threshold_night_flight', {
        price_diff: priceDifferenceString,
      });

    case MESSAGE_TYPE.MESSAGE_TYPE_OVER_THRESHOLD_DIRECT:
      return translate('ktxt_rank_explainability_outwith_threshold_direct');
    case MESSAGE_TYPE.MESSAGE_TYPE_OVER_THRESHOLD_FASTER:
      return translate('ktxt_rank_explainability_outwith_threshold_faster', {
        duration_diff: durationDifferenceString,
      });
    case MESSAGE_TYPE.MESSAGE_TYPE_OVER_THRESHOLD_NIGHT:
      return translate('ktxt_rank_explainability_outwith_threshold_night_flight');

    case MESSAGE_TYPE.MESSAGE_TYPE_CHEAPEST_AND_FASTEST:
      return translate('ktxt_rank_explainability_cheapest_and_fastest');
    case MESSAGE_TYPE.MESSAGE_TYPE_BEST_IS_FASTEST:
      return translate('ktxt_rank_explainability_best_is_fastest');
    case MESSAGE_TYPE.MESSAGE_TYPE_NEAR_FASTEST_CHEAPER: {
      if (timeDifference == null || timeDifference <= 0) {
        return null;
      }
      const timeDifferenceString = parseFlightDurationToString({ duration: timeDifference }, true);
      return translate('ktxt_rank_explainability_near_fastest_cheaper', {
        time_diff: timeDifferenceString,
        price_diff: priceDifferenceString,
      });
    }
    case MESSAGE_TYPE.MESSAGE_TYPE_SHORT_LAYOVER:
      return translate('ktxt_rank_explainability_short_layover', {
        price_diff: priceDifferenceString,
      });
    case MESSAGE_TYPE.MESSAGE_TYPE_GOOD_ARRIVAL_TIMES:
      return translate('ktxt_rank_explainability_good_arrival_times_static');
    case MESSAGE_TYPE.MESSAGE_TYPE_GENERIC_DURATION_AND_PRICE:
      return translate('ktxt_rank_explainability_generic_duration_and_price');

    case MESSAGE_TYPE.MESSAGE_TYPE_UNSPECIFIED:
    case MESSAGE_TYPE.MESSAGE_TYPE_NO_MESSAGE:
    default:
      return null;
  }
};

export const formatRankExplainabilityMessage = (
  messageType: RankExplainabilityMessageType | null | undefined,
  rankData: RankExplainabilityData,
): string | null => {
  if (
    messageType == null ||
    messageType === MESSAGE_TYPE.MESSAGE_TYPE_UNSPECIFIED ||
    messageType === MESSAGE_TYPE.MESSAGE_TYPE_NO_MESSAGE
  ) {
    return null;
  }

  if (!rankData) {
    return null;
  }

  return buildMessage(
    messageType,
    rankData.priceDifference,
    rankData.durationDifference,
    rankData.timeDifference,
  );
};
